import { Alert, Box, Paper, Stack } from '@mui/material'
import type { GridColDef, GridRowId } from '@mui/x-data-grid'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import { useAlertContext } from '@/contexts/AlertContext'
import { LatestConfigVersionsInfo } from '@/features/controller/configuration/components/LatestConfigVersionsInfo'
import { ControllerConfigurationTree } from '@/features/controller/configuration/components/tree/ControllerConfigurationTree'
import { ControllerHeader } from '@/features/controller/configuration/components/tree/ControllerHeader'
import { ControllerTooltip } from '@/features/controller/configuration/components/tree/ControllerTooltip'
import { NodeErrors } from '@/features/controller/configuration/components/tree/NodeErrors'
import type { TreeConfigChange } from '@/features/controller/configuration/components/tree/TreeReviewEditsDialog'
import { TreeReviewEditsDialog } from '@/features/controller/configuration/components/tree/TreeReviewEditsDialog'
import { useEditableControllerConfigurationModel } from '@/features/controller/configuration/hooks/useEditableControllerConfigurationModel'
import type { ControllerNode } from '@/features/controller/configuration/models/controllerConfigTree'
import type { ControllerConfigurationModel } from '@/features/controller/configuration/models/controllerConfiguration'
import { isTillquistEnabled } from '@/features/controller/configuration/models/raspiProperties/tillquist'
import { getControllerConfigType } from '@/features/controller/configuration/utils/controllerConfigTypes'
import { isSaveConflict } from '@/features/controller/configuration/utils/saveConflict'
import { findSerialPortConflict } from '@/features/controller/configuration/utils/serialPortUsage'
import { useRestartControllerMutation } from '@/features/controller/restart/hooks/useRestartControllerMutation'
import { ControllerRestartMethod } from '@/features/controller/restart/types'
import type { ControllerDescription } from '@/features/controller/types'
import { getElectricianViewUrl } from '@/features/controller/utils/electricianViewLinks'
import { StyledCustomDataGrid } from '@/features/resource/components/steering/StyledCustomDataGrid'
import CancelConfirmIconButtons from '@/features/site/components/CancelConfirmIconButtons'
import type { Site } from '@/features/site/types/site'

interface SingleControllerConfigTreeProps {
  controller: ControllerDescription
  site: Site | null
}

function hasAnyPowerMetersConfigured(configuration: ControllerConfigurationModel) {
  if ((configuration.appConfig?.powerMeters?.length ?? 0) > 0) {
    return true
  }
  if (configuration.raspiPropertiesConfig) {
    if (isTillquistEnabled(configuration.raspiPropertiesConfig.tillquistConfig)) {
      return true
    }
    for (const conn of configuration.raspiPropertiesConfig.legacyModbusConnections) {
      if (conn.meters.length > 0) {
        return true
      }
    }
  }
  return false
}

/*
 * This is a middle step towards having a table with the configuration of multiple controllers
 */
export function SingleControllerConfigTree({ controller, site }: Readonly<SingleControllerConfigTreeProps>) {
  const [expandedRowIds, setExpandedRowIds] = useState<Set<GridRowId>>(new Set([controller.id]))
  const [isReviewing, setIsReviewing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // Set when the last save stored app.json but not raspi-custom.properties
  const [partialSaveWarning, setPartialSaveWarning] = useState<string | null>(null)

  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()

  const {
    baseVersions,
    comment,
    configurationModel,
    isFetchingNewerVersion,
    isLoading,
    latestConfigurationVersions,
    onConfigurationModelUpdated,
    onConfigurationSourceUpdated,
    discardUnsavedChanges,
    hasNewerVersion,
    persistUnsavedChanges,
    setComment,
    unsavedChanges,
    sourceView,
  } = useEditableControllerConfigurationModel({
    controllerId: controller.id,
    onNewPowerMeterAdded: alertNewPowerMeterAdded,
  })

  const { restartController, isPending: isRestartingController } = useRestartControllerMutation(
    controller.id,
    ControllerRestartMethod.SSH,
  )

  async function onSave({ withRestart }: { withRestart: boolean }) {
    setIsSaving(true)
    setPartialSaveWarning(null)
    const warnPartiallySaved = () =>
      setPartialSaveWarning(
        t(
          withRestart
            ? 'controllers.configuration.tree.partially_saved_not_restarted'
            : 'controllers.configuration.tree.partially_saved',
          {
            savedConfigTypeName: getControllerConfigType(baseVersions.appJson.version!.configurationTypeId).name,
            unsavedConfigTypeName: getControllerConfigType(baseVersions.raspiProperties.version!.configurationTypeId)
              .name,
          },
        ),
      )
    try {
      if ((await persistUnsavedChanges({ onPartiallySaved: warnPartiallySaved })) === 'rebased') {
        // The review stays open, now comparing with the version saved meanwhile
        return false
      }
      pushAlert({
        message: t('controllers.configuration.update_success'),
        severity: 'success',
      })
      setIsReviewing(false)
      return true
    } catch (error) {
      if (isSaveConflict(error)) {
        pushAlert({
          message: t('controllers.configuration.update_error_conflict'),
          severity: 'error',
        })
      } else {
        pushAlert({
          message: t('controllers.configuration.update_error'),
          severity: 'error',
        })
      }
      return false
    } finally {
      setIsSaving(false)
    }
  }

  async function onSaveAndRestart() {
    if (await onSave({ withRestart: true })) {
      try {
        await restartController()
        // ControllerRestartDetails on the same page shares the restart state, and shows how the restart goes
        pushAlert({
          message: t('controllers.configuration.restart_requested'),
          severity: 'info',
        })
      } catch {
        pushAlert({
          message: t('controllers.configuration.restart_error'),
          severity: 'error',
        })
      }
    }
  }

  function alertNewPowerMeterAdded() {
    pushAlert({
      action: site && (
        <CustomButton
          color="inheritText"
          href={getElectricianViewUrl(site, controller)}
          target="_blank"
          variant="contained"
        >
          {t('controllers.configuration.power_meters.new_power_meter_added_alert.open_electrician_ui')}
        </CustomButton>
      ),
      isPersistent: true,
      message: t('controllers.configuration.power_meters.new_power_meter_added_alert.message'),
      severity: 'info',
      title: t('controllers.configuration.power_meters.new_power_meter_added_alert.title'),
    })
  }

  const columns: GridColDef<ControllerNode>[] = [
    {
      field: 'equipmentNumber',
      flex: 4,
      renderCell: ({ row }) => (
        <ControllerHeader
          configurationModel={configurationModel}
          configurationSourceView={sourceView}
          controllerDescription={row.controllerDescription}
          onConfigurationSourceUpdated={onConfigurationSourceUpdated}
        />
      ),
    },
    {
      field: 'errors',
      minWidth: 58,
      maxWidth: 58,
      renderCell: ({ row }) => <NodeErrors errors={row.errors} />,
    },
    {
      align: 'right',
      field: 'saveActions',
      // We can't allow flex width as that could hide the important buttons
      minWidth: 116,
      renderCell: () =>
        hasChanges ? (
          <Box padding={0.5}>
            <CancelConfirmIconButtons
              cancelDisabled={false}
              onCancelClick={discardUnsavedChanges}
              onConfimClick={() => setIsReviewing(true)}
            />
          </Box>
        ) : (
          <Stack padding={0.5}></Stack>
        ),
    },
  ]

  const hasChanges = Boolean(unsavedChanges.appJson) || Boolean(unsavedChanges.raspiProperties)
  const serialPortConflict = findSerialPortConflict(configurationModel)
  const changesToReview: TreeConfigChange[] = (['appJson', 'raspiProperties'] as const).flatMap((key) => {
    const change = unsavedChanges[key]
    const base = baseVersions[key]
    return change ? [{ baseVersion: base.version!, baseReplaced: base.replaced, value: change.value }] : []
  })
  const hasPowerMeters = hasAnyPowerMetersConfigured(configurationModel)

  return (
    <>
      {(['appJson', 'raspiProperties'] as const)
        .filter((key) => hasNewerVersion[key])
        .map((key) => (
          <Paper key={key}>
            <Alert severity="warning" variant="outlined">
              {t('controllers.configuration.newer_version_while_editing', {
                configTypeName: getControllerConfigType(baseVersions[key].version!.configurationTypeId).name,
              })}
            </Alert>
          </Paper>
        ))}
      <StyledCustomDataGrid
        columnHeaderHeight={0}
        columns={columns}
        detailPanelExpandedRowIds={expandedRowIds}
        disableColumnReorder={true}
        disableColumnSorting={true}
        getDetailPanelContent={() => (
          <ControllerConfigurationTree
            configuration={configurationModel}
            indent={true}
            onConfigurationUpdated={onConfigurationModelUpdated}
          />
        )}
        getDetailPanelHeight={() => 'auto'}
        getRowId={(row: ControllerNode) => row.controllerDescription.id}
        hideFooter={true}
        includeWrapper={true}
        isLoading={isLoading}
        rows={[{ controllerDescription: controller, errors: configurationModel.errors }]}
        style={hasChanges ? { border: '2px dashed lightblue' } : { border: '2px solid white' }}
        title={t('controllers.configuration.tree.title')}
        titleExtraInfo={<ControllerTooltip />}
        onDetailPanelExpandedRowIdsChange={setExpandedRowIds}
      />
      <LatestConfigVersionsInfo
        controllerId={controller.id}
        latestConfigurationVersions={latestConfigurationVersions}
      />
      <TreeReviewEditsDialog
        blockingErrors={serialPortConflict ? [serialPortConflict] : []}
        changes={changesToReview}
        comment={comment}
        controllerId={controller.id}
        isFetchingNewerVersion={isFetchingNewerVersion}
        isUpdating={isSaving || isRestartingController}
        open={isReviewing}
        partialSaveWarning={partialSaveWarning}
        onClose={() => {
          setIsReviewing(false)
          setPartialSaveWarning(null)
        }}
        onCommentChange={setComment}
        onSave={() => onSave({ withRestart: false })}
        onSaveAndRestart={onSaveAndRestart}
      />
      {!hasPowerMeters && (
        <Paper>
          <Alert severity="info" variant="outlined">
            {t('controllers.configuration.power_meters.no_power_meters')}
          </Alert>
        </Paper>
      )}
    </>
  )
}
